import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Button, Tag, Space, Typography,
  Tabs, Input, Select, Alert, Badge, Modal, Form, message,
  Spin, Empty, Tooltip, Popconfirm, Progress, Divider, Collapse, Timeline, List,
} from 'antd';
import {
  DatabaseOutlined, UserOutlined, TeamOutlined, ThunderboltOutlined,
  SyncOutlined, CheckCircleOutlined, CloseCircleOutlined,
  PlayCircleOutlined, ReloadOutlined, WarningOutlined,
  SendOutlined, FileTextOutlined, BulbOutlined, FilterOutlined,
  MergeOutlined, BookOutlined, RocketOutlined, StarOutlined, ToolOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const API = '/api/v1/cdp';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

const SOURCE_COLORS = { mssql: '#1890ff', odoo: '#722ed1', yeti: '#52c41a', gdcrm: '#fa8c16', nas: '#13c2c2' };
const SOURCE_LABELS = { mssql: '🗄️ 正航', odoo: '🔵 Odoo', yeti: '🐾 YetiForce', gdcrm: '📊 GDcrm', nas: '💾 NAS' };

export default function CDPPage() {
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [etlRuns, setEtlRuns] = useState([]);
  const [dedup, setDedup] = useState([]);
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [etlRunning, setEtlRunning] = useState(null);
  const [search, setSearch] = useState('');
  const [infraOnly, setInfraOnly] = useState(false);
  const [segModal, setSegModal] = useState(false);
  const [campModal, setCampModal] = useState(false);
  const [segForm] = Form.useForm();
  const [campForm] = Form.useForm();
  const [knowledge, setKnowledge] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [st, cu, er, dd, sg, ca, kb] = await Promise.all([
        fetch(`${API}/stats`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/customers?limit=100${infraOnly ? '&is_infra=true' : ''}${search ? `&search=${search}` : ''}`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/etl/runs?limit=20`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/dedup?status=pending`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/segments`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/campaigns`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/knowledge`, { headers: h() }).then(r => r.json()),
      ]);
      setStats(st); setCustomers(cu.customers || []); setEtlRuns(Array.isArray(er) ? er : []);
      setDedup(Array.isArray(dd) ? dd : []); setSegments(Array.isArray(sg) ? sg : []);
      setCampaigns(Array.isArray(ca) ? ca : []);
      setKnowledge(Array.isArray(kb) ? kb : []);
    } catch (e) { message.error('載入失敗: ' + e.message); }
    finally { setLoading(false); }
  }, [infraOnly, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runETL = async (source) => {
    setEtlRunning(source);
    try {
      await fetch(`${API}/etl/run/${source}`, { method: 'POST', headers: h() });
      message.success(`ETL ${source} 已啟動（背景執行）`);
      setTimeout(fetchAll, 3000);
    } catch (e) { message.error('ETL 啟動失敗: ' + e.message); }
    finally { setEtlRunning(null); }
  };

  const handleMerge = async (qid) => {
    await fetch(`${API}/dedup/${qid}/merge`, { method: 'POST', headers: h() });
    message.success('已合併'); fetchAll();
  };
  const handleReject = async (qid) => {
    await fetch(`${API}/dedup/${qid}/reject`, { method: 'POST', headers: h() });
    message.success('已拒絕'); fetchAll();
  };

  const createSegment = async (vals) => {
    const tags = vals.infra_tags ? vals.infra_tags.split(',').map(t => t.trim()) : [];
    await fetch(`${API}/segments`, {
      method: 'POST', headers: h(),
      body: JSON.stringify({
        name: vals.name, description: vals.description,
        filter_rules: { infra_tags: tags, is_infra_only: vals.infra_only, exclude_ai_infra_current: true },
      }),
    });
    message.success('分群已建立'); setSegModal(false); segForm.resetFields(); fetchAll();
  };

  const computeSegment = async (sid) => {
    const r = await fetch(`${API}/segments/${sid}/compute`, { method: 'POST', headers: h() }).then(r => r.json());
    message.success(`分群客戶數：${r.customer_count}`); fetchAll();
  };

  const approveCampaign = async (cid) => {
    await fetch(`${API}/campaigns/${cid}/approve`, { method: 'POST', headers: h() });
    message.success('活動已核准'); fetchAll();
  };

  const createCampaign = async (vals) => {
    await fetch(`${API}/campaigns`, {
      method: 'POST', headers: h(),
      body: JSON.stringify({ name: vals.name, product_tag: vals.product_tag, segment_id: vals.segment_id, notes: vals.notes }),
    });
    message.success('活動已建立'); setCampModal(false); campForm.resetFields(); fetchAll();
  };

  const customerCols = [
    { title: '公司名稱', dataIndex: 'company_name', ellipsis: true, width: 220,
      render: (n, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{n}</Text>
          {r.vat_number && <Text type="secondary" style={{ fontSize: 11 }}>統編: {r.vat_number}</Text>}
        </Space>
      )},
    { title: '來源', dataIndex: 'source_systems', width: 130,
      render: ss => (ss || []).map(s => <Tag key={s} color={SOURCE_COLORS[s]} style={{ fontSize: 10 }}>{SOURCE_LABELS[s] || s}</Tag>)},
    { title: 'Infra', dataIndex: 'is_infra_customer', width: 60, align: 'center',
      render: v => v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <Text type="secondary">-</Text>},
    { title: 'Infra 標籤', dataIndex: 'infra_tags', width: 160,
      render: tags => (tags || []).map(t => <Tag key={t} color="blue" style={{ fontSize: 10 }}>{t}</Tag>)},
    { title: '成交金額', dataIndex: 'total_transaction_amount', width: 110, align: 'right',
      render: v => v ? `$${Number(v).toLocaleString()}` : '-'},
    { title: '最近成交', dataIndex: 'last_transaction_at', width: 100,
      render: v => v && v !== 'None' ? new Date(v).toLocaleDateString('zh-TW') : '-'},
    { title: 'EDM', dataIndex: 'edm_unsubscribed', width: 55, align: 'center',
      render: v => v ? <Tag color="red" style={{ fontSize: 10 }}>退訂</Tag> : <Tag color="green" style={{ fontSize: 10 }}>訂閱</Tag>},
  ];

  const etlCols = [
    { title: '來源', dataIndex: 'source', width: 90, render: s => <Tag color={SOURCE_COLORS[s]}>{SOURCE_LABELS[s] || s}</Tag>},
    { title: '狀態', dataIndex: 'status', width: 80,
      render: s => <Tag color={s === 'done' ? 'green' : s === 'failed' ? 'red' : 'blue'}>{s}</Tag>},
    { title: '匯入', dataIndex: 'inserted', width: 60, render: v => <Text style={{ color: '#52c41a' }}>+{v}</Text>},
    { title: '更新', dataIndex: 'updated', width: 60, render: v => <Text style={{ color: '#1890ff' }}>~{v}</Text>},
    { title: '跳過', dataIndex: 'skipped', width: 60, render: v => <Text type="secondary">{v}</Text>},
    { title: '錯誤', dataIndex: 'error', ellipsis: true, render: v => v ? <Text type="danger" style={{ fontSize: 11 }}>{v}</Text> : '-'},
    { title: '時間', dataIndex: 'started_at', width: 140, render: v => new Date(v).toLocaleString('zh-TW')},
  ];

  const dedupCols = [
    { title: '公司 A', dataIndex: ['customer_a','name'], ellipsis: true,
      render: (_, r) => <Space direction="vertical" size={0}><Text strong>{r.customer_a?.name}</Text><Text type="secondary" style={{ fontSize: 11 }}>{r.customer_a?.vat}</Text></Space>},
    { title: '公司 B', dataIndex: ['customer_b','name'], ellipsis: true,
      render: (_, r) => <Space direction="vertical" size={0}><Text strong>{r.customer_b?.name}</Text><Text type="secondary" style={{ fontSize: 11 }}>{r.customer_b?.vat}</Text></Space>},
    { title: '信心', dataIndex: 'confidence', width: 80,
      render: v => <Progress percent={Math.round(v*100)} size="small" status={v >= 0.85 ? 'success' : 'normal'} />},
    { title: '方法', dataIndex: 'match_method', width: 90, render: v => <Tag>{v}</Tag>},
    { title: '操作', width: 120,
      render: (_, r) => (
        <Space>
          <Popconfirm title="確定合併這兩家公司？" onConfirm={() => handleMerge(r.id)}>
            <Button size="small" type="primary" icon={<MergeOutlined />}>合併</Button>
          </Popconfirm>
          <Button size="small" danger onClick={() => handleReject(r.id)}>拒絕</Button>
        </Space>
      )},
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <DatabaseOutlined style={{ marginRight: 8, color: '#722ed1' }} />
            客戶資料中台 CDP
          </Title>
          <Text type="secondary">Comboware 舊客戶整合 → Infra AI Upsell 引擎</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>重新整理</Button>
      </div>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        {[
          { title: '整合客戶總數', value: stats.total_customers, icon: <TeamOutlined />, color: '#722ed1' },
          { title: 'Infra 潛在客戶', value: stats.infra_customers, icon: <ThunderboltOutlined />, color: '#fa8c16' },
          { title: '待人工覆核', value: stats.dedup_pending, icon: <WarningOutlined />, color: '#ff4d4f' },
          { title: 'Infra 成交記錄', value: stats.infra_transactions, icon: <FileTextOutlined />, color: '#1890ff' },
        ].map((s, i) => (
          <Col span={6} key={i}>
            <Card size="small" style={{ borderTop: `3px solid ${s.color}` }}>
              <Statistic title={<Text style={{ fontSize: 12 }}>{s.title}</Text>} value={s.value ?? 0}
                prefix={<span style={{ color: s.color }}>{s.icon}</span>}
                valueStyle={{ color: s.color, fontSize: 24 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Alert
        type={stats.total_customers > 0 ? 'success' : 'warning'}
        showIcon style={{ marginBottom: 20 }}
        message={stats.total_customers > 0
          ? `資料中台運作中 — ${stats.total_customers} 筆客戶已整合，${stats.infra_customers} 筆 Infra 潛在名單`
          : '尚無客戶資料 — 請先到「開發調查表」填寫系統連線資訊，再執行 ETL 匯入'}
      />

      <Tabs items={[
        // ── Tab 1: ETL ──
        {
          key: 'etl',
          label: <span><SyncOutlined /> ETL 資料匯入</span>,
          children: (
            <div>
              <Card title="執行 ETL 匯入" size="small" style={{ marginBottom: 16 }}
                extra={<Button size="small" icon={<PlayCircleOutlined />} type="primary"
                  loading={etlRunning === 'all'} onClick={() => runETL('all')}>全部執行</Button>}>
                <Row gutter={12}>
                  {[
                    { key: 'mssql', label: '🗄️ 正航 MSSQL', desc: '最權威 — 已開發票成交記錄' },
                    { key: 'odoo', label: '🔵 Odoo CRM', desc: 'res.partner + sale.order' },
                    { key: 'yeti', label: '🐾 YetiForce CRM', desc: 'Accounts + Opportunities' },
                    { key: 'gdcrm', label: '📊 GDcrm', desc: 'CSV / API / DB' },
                    { key: 'nas', label: '💾 NAS 檔案', desc: '報價單 / 採購單掃描' },
                  ].map(src => (
                    <Col span={8} key={src.key} style={{ marginBottom: 8 }}>
                      <Card size="small" style={{ borderLeft: `3px solid ${SOURCE_COLORS[src.key]}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <Text strong style={{ fontSize: 13 }}>{src.label}</Text>
                            <br /><Text type="secondary" style={{ fontSize: 11 }}>{src.desc}</Text>
                          </div>
                          <Button size="small" icon={<PlayCircleOutlined />}
                            loading={etlRunning === src.key}
                            onClick={() => runETL(src.key)}>執行</Button>
                        </div>
                      </Card>
                    </Col>
                  ))}
                  <Col span={8}>
                    <Alert type="info" showIcon style={{ height: '100%' }}
                      message="需先填調查表"
                      description={<a href="/dev-survey" target="_blank">前往 開發調查表 →</a>} />
                  </Col>
                </Row>
              </Card>
              <Card title="ETL 執行記錄" size="small">
                <Table dataSource={etlRuns} rowKey="id" columns={etlCols}
                  size="small" pagination={{ pageSize: 10 }} loading={loading} />
              </Card>
            </div>
          ),
        },

        // ── Tab 2: Customers ──
        {
          key: 'customers',
          label: <span><TeamOutlined /> 客戶中台 <Badge count={stats.total_customers} color="#722ed1" /></span>,
          children: (
            <div>
              <Space style={{ marginBottom: 12 }}>
                <Input.Search placeholder="搜尋公司名稱/統編/Email" value={search}
                  onChange={e => setSearch(e.target.value)} onSearch={fetchAll} style={{ width: 280 }} />
                <Button icon={<FilterOutlined />}
                  type={infraOnly ? 'primary' : 'default'}
                  onClick={() => setInfraOnly(v => !v)}>
                  {infraOnly ? '✅ Infra 客戶' : '全部客戶'}
                </Button>
              </Space>
              <Table dataSource={customers} rowKey="id" columns={customerCols}
                size="small" pagination={{ pageSize: 20 }} loading={loading}
                scroll={{ x: 900 }} />
            </div>
          ),
        },

        // ── Tab 3: Dedup ──
        {
          key: 'dedup',
          label: <span><WarningOutlined /> 人工覆核 <Badge count={dedup.length} color="#ff4d4f" /></span>,
          children: (
            <div>
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={`${dedup.length} 筆疑似重複客戶待人工確認`}
                description="系統自動比對統編、公司名稱相似度，信心分數 < 80% 的需人工判斷是否為同一家公司。" />
              <Table dataSource={dedup} rowKey="id" columns={dedupCols}
                size="small" pagination={{ pageSize: 10 }} loading={loading} />
            </div>
          ),
        },

        // ── Tab 4: Segments ──
        {
          key: 'segments',
          label: <span><BulbOutlined /> 客戶分群</span>,
          children: (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button type="primary" icon={<BulbOutlined />} onClick={() => setSegModal(true)}>新建分群</Button>
              </div>
              <Row gutter={[12, 12]}>
                {segments.map(seg => (
                  <Col span={8} key={seg.id}>
                    <Card size="small" title={seg.name}
                      extra={<Button size="small" onClick={() => computeSegment(seg.id)}>計算人數</Button>}>
                      <Text type="secondary" style={{ fontSize: 12 }}>{seg.description}</Text>
                      <div style={{ marginTop: 8 }}>
                        <Text strong style={{ fontSize: 20, color: '#722ed1' }}>{seg.customer_count}</Text>
                        <Text type="secondary"> 位客戶</Text>
                      </div>
                      {seg.last_computed_at && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          更新：{new Date(seg.last_computed_at).toLocaleDateString('zh-TW')}
                        </Text>
                      )}
                    </Card>
                  </Col>
                ))}
                {segments.length === 0 && <Col span={24}><Empty description="尚無分群" /></Col>}
              </Row>
            </div>
          ),
        },

        // ── Tab 5: Campaigns ──
        {
          key: 'campaigns',
          label: <span><SendOutlined /> 升級銷售活動</span>,
          children: (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button type="primary" icon={<SendOutlined />} onClick={() => setCampModal(true)}>建立活動</Button>
              </div>
              <Table size="small" pagination={{ pageSize: 10 }} loading={loading}
                dataSource={campaigns} rowKey="id"
                columns={[
                  { title: '活動名稱', dataIndex: 'name', ellipsis: true },
                  { title: '產品', dataIndex: 'product_tag', render: t => <Tag color="purple">{t}</Tag>},
                  { title: '分群', dataIndex: 'segment_name', render: (n, r) => `${n || '-'} (${r.customer_count || 0}人)`},
                  { title: '狀態', dataIndex: 'status', render: s => (
                    <Tag color={{ draft:'default', pending_approval:'orange', approved:'blue', sent:'green' }[s] || 'default'}>{
                      { draft:'草稿', pending_approval:'待核准', approved:'✅ 已核准', sent:'已發送' }[s] || s
                    }</Tag>
                  )},
                  { title: '操作', width: 120, render: (_, r) => (
                    <Space>
                      {r.status === 'draft' && (
                        <Popconfirm title="核准此活動後將可發送 EDM，確定核准？"
                          onConfirm={() => approveCampaign(r.id)}>
                          <Button size="small" type="primary">核准</Button>
                        </Popconfirm>
                      )}
                      {r.status === 'approved' && (
                        <Tag color="blue">待發送</Tag>
                      )}
                    </Space>
                  )},
                ]} />
            </div>
          ),
        },

        // ── Tab 6: Knowledge Base ──
        {
          key: 'knowledge',
          label: <span><BookOutlined /> 銷售知識庫</span>,
          children: (
            <div>
              <Alert type="success" showIcon style={{ marginBottom: 20 }}
                message="NetAI 銷售知識庫 — 由 AI 分析 CDP 資料 + 產品資料後自動生成"
                description="以下 4 個問題的答案是根據 NetAI 銷售簡報 + Odoo 1,768 家客戶資料分析而來，可直接用於銷售策略制定。" />

              {knowledge.length === 0 ? <Spin /> : (
                <Collapse defaultActiveKey={knowledge.map(k => String(k.id))} accordion={false}>
                  {knowledge.map(kb => {
                    const c = kb.content || {};
                    const section = kb.section;
                    return (
                      <Collapse.Panel key={String(kb.id)}
                        header={
                          <Space>
                            {section === 'product' && <BulbOutlined style={{ color: '#1890ff' }} />}
                            {section === 'targeting' && <FilterOutlined style={{ color: '#722ed1' }} />}
                            {section === 'process' && <RocketOutlined style={{ color: '#52c41a' }} />}
                            {section === 'features' && <ToolOutlined style={{ color: '#fa8c16' }} />}
                            <Text strong style={{ fontSize: 15 }}>{kb.title}</Text>
                          </Space>
                        }>

                        {/* Q1: Product Value */}
                        {section === 'product' && (
                          <div>
                            <Alert type="info" message={c.summary} style={{ marginBottom: 16 }} showIcon />
                            <Row gutter={16} style={{ marginBottom: 16 }}>
                              {(c.roi_numbers || []).map((r, i) => (
                                <Col span={6} key={i}>
                                  <Card size="small" style={{ textAlign: 'center', borderTop: '3px solid #1890ff' }}>
                                    <Text style={{ fontSize: 24, fontWeight: 700, color: '#1890ff' }}>{r.value}</Text>
                                    <br /><Text type="secondary" style={{ fontSize: 12 }}>{r.metric}</Text>
                                  </Card>
                                </Col>
                              ))}
                            </Row>
                            <Divider>三種方案</Divider>
                            <Row gutter={12}>
                              {(c.plans || []).map((p, i) => (
                                <Col span={8} key={i}>
                                  <Card size="small" title={p.plan} style={{ borderTop: `3px solid ${i===1?'#52c41a':'#1890ff'}` }}>
                                    <Text strong style={{ color: '#722ed1' }}>{p.price}</Text><br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>{p.target}</Text><br />
                                    <Tag color={i===1?'green':'blue'} style={{ marginTop: 8 }}>{p.highlight}</Tag>
                                  </Card>
                                </Col>
                              ))}
                            </Row>
                            <Divider>痛點對應</Divider>
                            {(c.pain_points || []).map((pp, i) => (
                              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, padding: '8px 12px', background: '#fafafa', borderRadius: 8 }}>
                                <Text style={{ fontSize: 20 }}>{pp.icon}</Text>
                                <div>
                                  <Text strong style={{ fontSize: 13 }}>{pp.pain}</Text>
                                  <br /><Tag color="green" style={{ marginTop: 4 }}>✅ {pp.value}</Tag>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Q2: Targeting */}
                        {section === 'targeting' && (
                          <div>
                            <Alert type="info" message={c.summary} style={{ marginBottom: 16 }} showIcon />
                            {(c.segments || []).map((seg, i) => (
                              <Card key={i} size="small" style={{ marginBottom: 12,
                                borderLeft: `4px solid ${['#ff4d4f','#fa8c16','#fadb14','#52c41a','#1890ff'][i] || '#ccc'}` }}>
                                <Space align="start">
                                  <Tag style={{ fontSize: 12, whiteSpace: 'pre' }}>{seg.priority}</Tag>
                                  <div>
                                    <Text strong>{seg.name}</Text>
                                    <br /><Text type="secondary" style={{ fontSize: 12 }}>{seg.why}</Text>
                                    <br /><Text style={{ fontSize: 12, color: '#52c41a' }}>📋 {seg.approach}</Text>
                                    {seg.example_customers && (
                                      <div style={{ marginTop: 4 }}>
                                        {seg.example_customers.map((e, j) => <Tag key={j} style={{ fontSize: 11 }}>{e}</Tag>)}
                                      </div>
                                    )}
                                  </div>
                                  <Tag color="blue" style={{ marginLeft: 'auto' }}>{seg.win_rate}</Tag>
                                </Space>
                              </Card>
                            ))}
                          </div>
                        )}

                        {/* Q3: Sales Process */}
                        {section === 'process' && (
                          <div>
                            <Alert type="info" message={c.summary} style={{ marginBottom: 16 }} showIcon />
                            <Timeline>
                              {(c.funnel || []).map((step, i) => (
                                <Timeline.Item key={i} color={i < 2 ? 'green' : i < 5 ? 'blue' : 'gray'}>
                                  <Text strong>Step {step.step}: {step.stage}</Text>
                                  <br />
                                  <Tag color="purple">{step.platform_feature}</Tag>
                                  <br />
                                  <Text style={{ fontSize: 12 }}>{step.action}</Text>
                                  <br />
                                  <Space style={{ marginTop: 4 }}>
                                    <Tag color="orange">{step.time}</Tag>
                                    <Tag color="green">KPI: {step.kpi}</Tag>
                                  </Space>
                                </Timeline.Item>
                              ))}
                            </Timeline>
                            <Divider>平台功能對應</Divider>
                            {Object.entries(c.platform_summary || {}).map(([feat, desc], i) => (
                              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                <Tag color="blue" style={{ fontSize: 11, flexShrink: 0 }}>{feat}</Tag>
                                <Text type="secondary" style={{ fontSize: 12 }}>{desc}</Text>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Q4: Features */}
                        {section === 'features' && (
                          <div>
                            <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                              message={c.summary} />
                            <Alert type="success" showIcon style={{ marginBottom: 16 }}
                              message="本週可立即執行的 3 件事"
                              description={
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {(c.quick_wins?.items || []).map((item, i) => <li key={i} style={{ fontSize: 12 }}>{item}</li>)}
                                </ul>
                              } />
                            <Row gutter={[12, 12]}>
                              {(c.features || []).map((feat, i) => (
                                <Col span={12} key={i}>
                                  <Card size="small" style={{ borderLeft: `4px solid ${feat.priority.includes('P0')?'#ff4d4f':feat.priority.includes('P1')?'#fa8c16':feat.priority.includes('P2')?'#fadb14':'#52c41a'}` }}>
                                    <Space direction="vertical" size={4}>
                                      <Space>
                                        <Tag>{feat.priority}</Tag>
                                        <Text strong style={{ fontSize: 13 }}>{feat.name}</Text>
                                      </Space>
                                      <Text style={{ fontSize: 12 }}>{feat.description}</Text>
                                      <Tag color="green" style={{ fontSize: 11 }}>✅ {feat.impact}</Tag>
                                      <Space>
                                        <Tag color="blue">⏱ {feat.est_dev}</Tag>
                                      </Space>
                                    </Space>
                                  </Card>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        )}
                      </Collapse.Panel>
                    );
                  })}
                </Collapse>
              )}
            </div>
          ),
        },
      ]} />

      {/* Segment modal */}
      <Modal title="新建客戶分群" open={segModal} onOk={() => segForm.submit()}
        onCancel={() => setSegModal(false)} okText="建立">
        <Form form={segForm} layout="vertical" onFinish={createSegment}>
          <Form.Item name="name" label="分群名稱" rules={[{ required: true }]}>
            <Input placeholder="e.g. Infra 升級目標客戶 2026" />
          </Form.Item>
          <Form.Item name="description" label="說明">
            <Input placeholder="這個分群的定義" />
          </Form.Item>
          <Form.Item name="infra_tags" label="Infra 標籤篩選（逗號分隔）">
            <Input placeholder="e.g. server,network,it_outsourcing" />
          </Form.Item>
          <Form.Item name="infra_only" valuePropName="checked">
            <input type="checkbox" /> 僅限 Infra 客戶（曾購買 infra 產品）
          </Form.Item>
        </Form>
      </Modal>

      {/* Campaign modal */}
      <Modal title="建立升級銷售活動" open={campModal} onOk={() => campForm.submit()}
        onCancel={() => setCampModal(false)} okText="建立">
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="活動建立後需要人工核准才能發送，不會自動發送 EDM。" />
        <Form form={campForm} layout="vertical" onFinish={createCampaign}>
          <Form.Item name="name" label="活動名稱" rules={[{ required: true }]}>
            <Input placeholder="e.g. NetAI 升級推播 2026Q3" />
          </Form.Item>
          <Form.Item name="product_tag" label="推廣產品" rules={[{ required: true }]}>
            <Select placeholder="選擇產品">
              <Select.Option value="netai">NetAI — AI 網路監控</Select.Option>
              <Select.Option value="ai_infra">AI Infra Platform</Select.Option>
              <Select.Option value="comboware_ai">Comboware AI 全套</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="segment_id" label="目標分群" rules={[{ required: true }]}>
            <Select placeholder="選擇分群">
              {segments.map(s => (
                <Select.Option key={s.id} value={s.id}>{s.name} ({s.customer_count} 人)</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="備註">
            <TextArea rows={2} placeholder="活動說明" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
